import { useGeneralSettingsFormData } from "@/api/cms";
import {
  createShellFlowTemplate,
  getShellFlow,
  listPuckPages,
  setShellHeaderSpacing,
  upsertShellFlow,
  type ShellFlowStep,
} from "@/api/cms-puck/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cmsNew, useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase, puckPageStatusLabel } from "@/lib/cms/cms-new-phrases";

const FLOW_KEY = ["cms-puck", "shell-flow"] as const;
const PAGES_KEY = ["cms-puck", "pages"] as const;
const NONE_VALUE = "__none__";

/** Clés + fallbacks — ne pas résoudre cmsNew() au niveau module (bundle i18n async). */
const STEP_META: Record<
  ShellFlowStep,
  {
    labelKey: CmsNewKey;
    labelFallback: string;
    titleKey: CmsNewKey;
    titleFallback: string;
    subtitleKey: CmsNewKey;
    subtitleFallback: string;
    helpKey: CmsNewKey;
    helpFallback: string;
  }
> = {
  HEADER: {
    labelKey: "cms.new.auto.label.en_tete",
    labelFallback: "En-tête",
    titleKey: "cms.new.auto.title.en_tete",
    titleFallback: "En-tête",
    subtitleKey: "cms.new.auto.subtitle.en_tete_template",
    subtitleFallback:
      "Template CMS avancé pour le bandeau global du site (marque, menu, actions).",
    helpKey: "cms.new.auto.description.en_tete_sans_liaison",
    helpFallback:
      "Sans liaison, le site affiche l'en-tête classique (TopbarArtist). Avec un template publié, le chrome header est entièrement piloté par le CMS avancé.",
  },
  FOOTER: {
    labelKey: "cms.new.auto.label.pied_de_page",
    labelFallback: "Pied de page",
    titleKey: "cms.new.auto.title.pied_de_page",
    titleFallback: "Pied de page",
    subtitleKey: "cms.new.auto.subtitle.pied_de_page_template",
    subtitleFallback:
      "Template CMS avancé pour le pied de page global (menu footer, mentions, réseaux).",
    helpKey: "cms.new.auto.description.pied_de_page_sans_liaison",
    helpFallback:
      "Sans liaison, le site affiche le pied de page classique (FooterArtist). Avec un template publié, le chrome footer est entièrement piloté par le CMS avancé.",
  },
};

export function shellRoleBadgeLabel(step: ShellFlowStep): string {
  const meta = STEP_META[step];
  return cmsNew(meta.labelKey, meta.labelFallback);
}

function FlowRow({
  step: _step,
  label,
  value,
  templatePages,
  onAssign,
  onCreateTemplate,
  saving,
  creating,
}: {
  step: ShellFlowStep;
  label: string;
  value: string | null;
  templatePages: { id: string; name: string; status: string }[];
  onAssign: (pageId: string | null) => void;
  onCreateTemplate: () => void;
  saving: boolean;
  creating: boolean;
}) {
  const cmsNew = useCmsNew();
  const navigate = useNavigate();
  const assigned = templatePages.find((p) => p.id === value);
  const isDraft = assigned && assigned.status !== "PUBLISHED";

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <Label className="text-sm font-medium text-slate-800">{label}</Label>
      <Select
        value={value ?? NONE_VALUE}
        onValueChange={(next) =>
          onAssign(next === NONE_VALUE ? null : next)
        }
        disabled={saving || creating}
      >
        <SelectTrigger>
          <SelectValue placeholder={cmsNew("cms.new.auto.placeholder.aucune_chrome_classique", "Aucune (chrome classique)")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>{cmsNew("cms.new.auto.selectItem.aucune_chrome_classique", "Aucune (chrome classique)")}</SelectItem>
          {templatePages.map((page) => (
            <SelectItem key={page.id} value={page.id}>
              {page.name}
              {page.status !== "PUBLISHED" ? ` (${puckPageStatusLabel(cmsNew, page.status)})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isDraft ? (
        <p className="text-xs text-amber-700">
          {cmsPhrase(cmsNew, "draftPublishHint")}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        {value ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            onClick={() => navigate(`/cms/advanced/${value}`)}
          >
            <ExternalLink size={14} />
            {cmsPhrase(cmsNew, "editTemplate")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1"
            disabled={creating}
            onClick={onCreateTemplate}
          >
            <Plus size={14} />
            {creating ? cmsPhrase(cmsNew, "creating") : cmsPhrase(cmsNew, "createTemplate")}
          </Button>
        )}
      </div>
    </div>
  );
}

export function CMSAdvancedShellFlowZone({ step }: { step: ShellFlowStep }) {
  const cmsNew = useCmsNew();
  const stepMeta = STEP_META[step];
  const label = cmsNew(stepMeta.labelKey, stepMeta.labelFallback);
  const title = cmsNew(stepMeta.titleKey, stepMeta.titleFallback);
  const subtitle = cmsNew(stepMeta.subtitleKey, stepMeta.subtitleFallback);
  const help = cmsNew(stepMeta.helpKey, stepMeta.helpFallback);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: generalSettings } = useGeneralSettingsFormData();
  const flowQuery = useQuery({ queryKey: FLOW_KEY, queryFn: getShellFlow });
  const pagesQuery = useQuery({
    queryKey: PAGES_KEY,
    queryFn: () => listPuckPages(),
  });

  const saveMutation = useMutation({
    mutationFn: upsertShellFlow,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FLOW_KEY });
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: `${label} ${cmsNew("cms.new.shell.savedSuffix", "enregistré")}`,
        variant: "success",
        duration: 1600,
      });
    },
    onError: () => {
      toast({
        title: cmsNew("cms.new.auto.title.impossible_denregistrer_la_liaison", "Impossible d'enregistrer la liaison"),
        variant: "error",
      });
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: createShellFlowTemplate,
    onSuccess: ({ page }) => {
      void queryClient.invalidateQueries({ queryKey: FLOW_KEY });
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew(
          "cms.new.flow.templateCreatedFor",
          `Template ${label.toLowerCase()} créé`,
        ),
        variant: "success",
        duration: 1600,
      });
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : cmsPhrase(cmsNew, "createTemplateFailed");
      toast({ title: message, variant: "error" });
    },
  });

  const spacingMutation = useMutation({
    mutationFn: setShellHeaderSpacing,
    onSuccess: (next) => {
      queryClient.setQueryData(FLOW_KEY, next);
      // Le réglage vit dans le layout de la page d'en-tête. Le cache de son
      // éditeur est supprimé, pas seulement invalidé : Puck se monterait sur
      // l'ancien layout et l'enregistrerait par-dessus le réglage.
      const headerPageId = next.find((b) => b.step === "HEADER")?.pageId;
      if (headerPageId) {
        queryClient.removeQueries({
          queryKey: ["cms-puck", "page", headerPageId],
        });
      }
    },
    onError: () => {
      toast({
        title: cmsNew(
          "cms.new.auto.title.impossible_denregistrer_la_liaison",
          "Impossible d'enregistrer la liaison",
        ),
        variant: "error",
      });
    },
  });

  const bindings = flowQuery.data ?? [];
  const pages = pagesQuery.data ?? [];
  const templatePages = pages.filter((p) => (p.shellFlowRoles?.length ?? 0) > 0);
  const binding = bindings.find((b) => b.step === step);
  const pageId = binding?.pageId ?? null;

  return (
    <CmsAdminShell title={title} subtitle={subtitle}>
      <div className="space-y-6 px-6 py-4">
        <p className="text-sm text-slate-600">{help}</p>

        {flowQuery.isLoading || pagesQuery.isLoading ? (
          <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : (
          <FlowRow
            step={step}
            label={label}
            value={pageId}
            templatePages={templatePages}
            saving={saveMutation.isPending}
            creating={createTemplateMutation.isPending}
            onAssign={(nextPageId) =>
              saveMutation.mutate({ step, pageId: nextPageId })
            }
            onCreateTemplate={() =>
              createTemplateMutation.mutate({
                step,
                lang: generalSettings?.defaultWebsiteLanguage,
              })
            }
          />
        )}

        {step === "HEADER" && pageId ? (
          <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4">
            <div className="space-y-1">
              <Label htmlFor="shell-header-spacing" className="text-sm font-medium text-slate-800">
                {cmsNew(
                  "cms.new.shell.headerSpacing.label",
                  "Espace de l'ancien site sous l'en-tête",
                )}
              </Label>
              <p className="text-xs text-slate-500">
                {cmsNew(
                  "cms.new.shell.headerSpacing.hint",
                  "Activé : les pages commencent plus bas, comme sur l'ancien site. Désactivé : elles commencent juste sous l'en-tête.",
                )}
              </p>
            </div>
            <Switch
              id="shell-header-spacing"
              checked={binding?.headerSpacing === "classic"}
              disabled={spacingMutation.isPending}
              onCheckedChange={(checked) =>
                spacingMutation.mutate(checked ? "classic" : "fit")
              }
            />
          </div>
        ) : null}

        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
          <p className="font-medium text-slate-800">{cmsNew("cms.new.auto.text.blocs_disponibles", "Blocs disponibles")}</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            <li>
              <strong>{cmsNew("cms.new.auto.text.marque_du_site", "Marque du site")}</strong> — {cmsNew("cms.new.auto.text.marque_du_site_desc", "titre / sous-titre (homepage CMS)")}
            </li>
            <li>
              <strong>{cmsNew("cms.new.auto.text.menu", "Menu")}</strong> — {cmsNew("cms.new.auto.text.menu_primary_footer", "menu primary ou footer configuré dans")}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() => window.open("/cms/advanced/menus", "_blank")}
              >
                {cmsNew("cms.new.auto.text.menus", "Menus")}
              </button>
            </li>
            {step === "HEADER" ? (
              <>
                <li>
                  <strong>{cmsNew("cms.new.auto.text.profil_connexion", "Profil / Connexion")}</strong> — {cmsNew("cms.new.auto.text.profil_connexion_desc", "compte connecté ou lien connexion")}
                </li>
                <li>
                  <strong>{cmsNew("cms.new.auto.text.panier", "Panier")}</strong> — {cmsNew("cms.new.auto.text.panier_desc", "icône boutique (si articles)")}
                </li>
                <li>
                  <strong>{cmsNew("cms.new.auto.text.selecteur_de_langue", "Sélecteur de langue")}</strong> — {cmsPhrase(cmsNew, "shellLangSelectorDesc")}
                </li>
              </>
            ) : (
                <li>
                  <strong>{cmsNew("cms.new.auto.text.contenu_pied_de_page", "Contenu pied de page")}</strong> — {cmsNew("cms.new.auto.text.contenu_pied_de_page_desc", "réseaux, mentions légales, archives")}
                </li>
            )}
          </ul>
          <p className="mt-2">
            {cmsNew(
              "cms.new.shell.chromeAppearanceHint",
              "Les couleurs et séparateurs du chrome restent pilotés par l'onglet Apparence.",
            )}
          </p>
        </div>
      </div>
    </CmsAdminShell>
  );
}
